"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const galleryItems = [
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_185ed2ca0-1769490279849.png",
  alt: "Player serving on pickleball court with focused athletic stance",
  label: "Pickleball",
  size: "large"
},
{
  src: "https://images.unsplash.com/photo-1710675133629-e3cae42292ef",
  alt: "Swimmer performing butterfly stroke in crystal clear pool water",
  label: "Riverpool",
  size: "small"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f40b9759-1772077752440.png",
  alt: "Athlete performing strength training in modern gym facility",
  label: "Fitness Studio",
  size: "small"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_107286287-1772077751786.png",
  alt: "Tennis player executing a powerful forehand on professional court",
  label: "Tennis",
  size: "medium"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6b532c7-1772077752333.png",
  alt: "Group fitness class in session with trainer leading members",
  label: "Community",
  size: "medium"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_178dda661-1772077754291.png",
  alt: "Healthy food and smoothies at Barebase Café recovery station",
  label: "Barebase Café",
  size: "small"
}];


export default function GallerySection() {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    cardRefs.current.forEach((card) => {if (card) observer.observe(card);});
    return () => cardRefs.current.forEach((card) => {if (card) observer.unobserve(card);});
  }, []);

  const handleSpotlight = (e: React.MouseEvent<HTMLDivElement>) => {
    const spotlight = e.currentTarget.querySelector(".gallery-spotlight") as HTMLElement;
    if (!spotlight) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width * 100;
    const y = (e.clientY - rect.top) / rect.height * 100;
    spotlight.style.setProperty("--mx", `${x}%`);
    spotlight.style.setProperty("--my", `${y}%`);
  };

  return (
    <section
      id="gallery"
      className="relative py-28"
      style={{ background: "linear-gradient(180deg, var(--navy-mid) 0%, var(--navy-dark) 100%)" }}>
      
      {/* Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 50% 100%, rgba(224,96,49,0.05) 0%, transparent 60%)"
        }} />
      

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="section-label mb-4">Energy in Motion</p>
          <h2 className="headline-lg text-white">
            Life at{" "}
            <span className="gradient-text">Play.XP</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-xl mx-auto">
            Every session. Every victory. Every moment of community.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4" style={{ gridTemplateRows: "auto" }}>
          {/* Large item - full width on mobile, spans 1 col and 2 rows on md+ */}
          <div
            ref={(el) => {cardRefs.current[0] = el;}}
            className="reveal gallery-item md:row-span-2"
            style={{ height: "280px" }}
            onMouseMove={handleSpotlight}>
            
            <AppImage
              src={galleryItems[0].src}
              alt={galleryItems[0].alt}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 33vw" />
            
            <div className="gallery-spotlight" />
            <div className="gallery-overlay" />
            <div className="absolute bottom-4 left-4 z-10">
              <span
                className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                style={{ background: "var(--terracotta)", color: "white" }}>
                
                {galleryItems[0].label}
              </span>
            </div>
          </div>

          {/* Remaining items */}
          {galleryItems.slice(1).map((item, i) =>
          <div
            key={item.label}
            ref={(el) => {cardRefs.current[i + 1] = el;}}
            className={`reveal stagger-${Math.min(i + 1, 5)} gallery-item`}
            style={{ height: "200px" }}
            onMouseMove={handleSpotlight}>
            
              <AppImage
              src={item.src}
              alt={item.alt}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 33vw" />
            
              <div className="gallery-spotlight" />
              <div className="gallery-overlay" />
              <div className="absolute bottom-3 left-3 z-10">
                <span
                className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)", color: "white" }}>
                
                  {item.label}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}